import { ArrowRight, Briefcase } from 'lucide-react'
import { DateValue, EmptyState } from '@/components/common'
import { Card } from '@/components/ui/card'
import { ASSIGNMENT_REASONS, type AssignmentView } from '../types'
import { cn } from '@/lib/utils'

/**
 * Where somebody has sat, and when.
 *
 * <p>Reads as a history rather than a list of records, because the question it answers is
 * "what changed and when" — and the reason a row exists is usually more interesting than
 * the fields on it. Only what actually changed between one row and the next is highlighted;
 * repeating every unchanged field would bury the move in its own context.
 */
export function AssignmentTimeline({ assignments }: { assignments: AssignmentView[] }) {
  if (assignments.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Briefcase}
          title="No assignment history"
          description="An employee always has at least one — this profile predates the assignment model, or its opening row was removed."
        />
      </Card>
    )
  }

  // Newest first, so the current placement is the first thing read.
  const rows = [...assignments].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))

  return (
    <ol className="space-y-0">
      {rows.map((assignment, index) => {
        const previous = rows[index + 1]
        return (
          <li key={assignment.id} className="relative flex gap-4 ps-1">
            {/* The rail, drawn per row rather than as one element so the last row's line
                stops at its marker instead of running off the end. */}
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'mt-1.5 size-3 shrink-0 rounded-full ring-4',
                  assignment.current
                    ? 'bg-[var(--color-primary)] ring-[var(--color-primary-soft)]'
                    : 'bg-[var(--color-border)] ring-[var(--color-page)]',
                )}
                aria-hidden="true"
              />
              {index < rows.length - 1 && (
                <span className="w-px flex-1 bg-[var(--color-border)]" aria-hidden="true" />
              )}
            </div>

            <Card className={cn('mb-3 flex-1 p-4', assignment.current && 'border-[var(--color-primary)]')}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-[var(--color-heading)]">
                    {ASSIGNMENT_REASONS[assignment.reason]}
                    {assignment.current && (
                      <span className="ms-2 rounded-full bg-[var(--color-primary-soft)] px-2 py-0.5 text-xs font-normal text-[var(--color-primary)]">
                        Current
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-sm text-[var(--color-body)]">
                    <DateValue value={assignment.effectiveFrom} />
                    {' — '}
                    {assignment.effectiveTo ? <DateValue value={assignment.effectiveTo} /> : 'present'}
                  </p>
                </div>
                {assignment.fte < 1 && (
                  <span className="text-xs text-[var(--color-muted)]">
                    {(assignment.fte * 100).toFixed(0)}% FTE
                  </span>
                )}
              </div>

              <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <Field label="Department" value={assignment.departmentName} was={previous?.departmentName} />
                <Field label="Designation" value={assignment.designationName} was={previous?.designationName} />
                <Field label="Grade" value={assignment.gradeName} was={previous?.gradeName} />
                <Field label="Site" value={assignment.locationName} was={previous?.locationName} />
                <Field label="Cost centre" value={assignment.costCenterName} was={previous?.costCenterName} />
                <Field label="Reports to" value={assignment.managerName} was={previous?.managerName} />
                {assignment.establishmentName && (
                  <Field
                    label="Establishment"
                    value={assignment.establishmentName}
                    was={previous?.establishmentName}
                  />
                )}
              </dl>

              {assignment.notes && (
                <p className="mt-3 text-sm italic text-[var(--color-muted)]">{assignment.notes}</p>
              )}
            </Card>
          </li>
        )
      })}
    </ol>
  )
}

/** One attribute, showing what it replaced when the previous row differed. */
function Field({ label, value, was }: { label: string; value: string | null; was?: string | null }) {
  if (!value && !was) {
    return null
  }
  const changed = was !== undefined && was !== value

  return (
    <div className="flex gap-2">
      <dt className="min-w-28 shrink-0 text-[var(--color-muted)]">{label}</dt>
      <dd className={cn('text-[var(--color-heading)]', changed && 'font-medium')}>
        {changed && was && (
          <>
            <span className="text-[var(--color-muted)] line-through">{was}</span>
            <ArrowRight className="mx-1 inline size-3 text-[var(--color-muted)]" aria-hidden="true" />
          </>
        )}
        {value ?? '—'}
      </dd>
    </div>
  )
}
